import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { checkActionsPerms, hasPermission, hasScopedPermission, type PermissionModel } from './model';
import type { PermissionResource, PermissionType } from './permission-types';
import { effectivePermissionsQuery } from './permission.queries';

type ResourceArg = PermissionResource | readonly PermissionResource[];

export interface CanOptions {
  /** Semântica de `hasScopedPermission` (a ação presa ao recurso); o legado ligava por `strictPermission`. */
  strict?: boolean;
}

/** Checa contra um modelo já carregado (útil em `beforeLoad` e em listas de ações). */
export function can(model: readonly PermissionModel[] | undefined, resource: ResourceArg, type: PermissionType, options: CanOptions = {}) {
  return options.strict ? hasScopedPermission(model, resource, type) : hasPermission(model, resource, type);
}

/** Permissões efetivas + `can` ligado a elas. Só chame dentro da área autenticada. */
export function usePermissions({ enabled = true }: { enabled?: boolean } = {}) {
  const query = useQuery({ ...effectivePermissionsQuery(), enabled });
  const model = query.data?.model;
  return {
    ...query,
    model,
    can: (resource: ResourceArg, type: PermissionType, options?: CanOptions) => can(model, resource, type, options),
    hasActions: (actionPrefix: string, resource: PermissionResource) => checkActionsPerms(actionPrefix, model, resource),
  };
}

/** `true` se o usuário pode; `false` enquanto carrega, como o `hasPermission` do legado. */
export function useCan(resource: ResourceArg, type: PermissionType, options?: CanOptions): boolean {
  return usePermissions().can(resource, type, options);
}

interface CanProps extends CanOptions {
  resource: ResourceArg;
  type: PermissionType;
  children: ReactNode;
  fallback?: ReactNode;
}

/** Mostra `children` só com a permissão; `fallback` (nada, por padrão) no resto, inclusive carregando. */
export function Can({ resource, type, strict, children, fallback = null }: CanProps) {
  const allowed = useCan(resource, type, strict === undefined ? {} : { strict });
  return <>{allowed ? children : fallback}</>;
}
